/**
 * Accept Invite Page
 * L0-CMD-2026-0120-011 - Team invitation acceptance flow
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface InvitationInfo {
  email: string;
  company_name: string;
  company_logo: string | null;
  inviter_name: string;
  role: string;
  expires_at: string;
}

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, login } = useAuth();

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Login form state
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchInvitation();
    }
  }, [token]);

  useEffect(() => {
    if (invitation) {
      setEmail(invitation.email);
    }
  }, [invitation]);

  const fetchInvitation = async () => {
    try {
      const response = await fetch(`/api/companies/invite/${token}`);
      const data = await response.json();

      if (!response.ok) {
        if (data.expired) {
          setError('This invitation has expired. Please ask for a new one.');
        } else if (data.used) {
          setError('This invitation has already been used.');
        } else {
          setError(data.error || 'Invalid invitation');
        }
        return;
      }

      setInvitation(data);
    } catch (err) {
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    try {
      await login(email, password);
      // After login, try to accept the invitation
      await acceptInvitation();
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  const acceptInvitation = async () => {
    setAccepting(true);
    setError(null);

    try {
      const authToken = localStorage.getItem('access_token');
      const response = await fetch(`/api/companies/invite/${token}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept invitation');
      }

      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
        window.location.reload();
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="invite-page">
        <div className="invite-card loading">
          <div className="spinner"></div>
          <p>Loading invitation...</p>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="invite-page">
        <div className="invite-card error">
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h2>Invitation Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/login')} className="btn-primary">
            Go to Login
          </button>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  if (success) {
    return (
      <div className="invite-page">
        <div className="invite-card success">
          <div className="success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h2>Welcome to {invitation?.company_name}!</h2>
          <p>You've successfully joined the team. Redirecting...</p>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="invite-card">
        {/* Company Logo */}
        <div className="company-logo">
          {invitation?.company_logo ? (
            <img src={invitation.company_logo} alt={invitation.company_name} />
          ) : (
            <div className="logo-placeholder">
              {invitation?.company_name?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <h1>You're Invited!</h1>
        <p className="invite-message">
          <strong>{invitation?.inviter_name}</strong> has invited you to join
          <strong> {invitation?.company_name}</strong> as a <strong>{invitation?.role}</strong>.
        </p>

        {error && (
          <div className="error-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            {error}
          </div>
        )}

        {isAuthenticated ? (
          // Already logged in - just accept
          <div className="action-section">
            <p className="logged-in-as">
              Logged in as <strong>{user?.email}</strong>
            </p>
            <button
              onClick={acceptInvitation}
              className="btn-primary"
              disabled={accepting}
            >
              {accepting ? 'Joining...' : 'Accept Invitation'}
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Cancel
            </button>
          </div>
        ) : showLogin ? (
          // Login form
          <form onSubmit={handleLogin} className="login-form">
            {loginError && (
              <div className="error-banner small">
                {loginError}
              </div>
            )}
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              required
            />
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              required
            />
            <button type="submit" className="btn-primary">
              Login & Accept
            </button>
            <button
              type="button"
              onClick={() => setShowLogin(false)}
              className="btn-secondary"
            >
              Back
            </button>
          </form>
        ) : (
          // Initial options
          <div className="action-section">
            <button onClick={() => setShowLogin(true)} className="btn-primary">
              Login to Accept
            </button>
            <p className="or-divider">or</p>
            <button
              onClick={() => navigate(`/register?invite=${token}&email=${encodeURIComponent(invitation?.email || '')}`)}
              className="btn-secondary"
            >
              Create Account
            </button>
          </div>
        )}

        <p className="expires-note">
          This invitation expires on {new Date(invitation?.expires_at || '').toLocaleDateString()}
        </p>
      </div>
      <style>{pageStyles}</style>
    </div>
  );
};

const pageStyles = `
  .invite-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 20px;
  }

  .invite-card {
    background: #1e293b;
    border-radius: 16px;
    padding: 40px;
    width: 100%;
    max-width: 420px;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  }

  .invite-card.loading,
  .invite-card.error,
  .invite-card.success {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #334155;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-icon {
    color: #ef4444;
  }

  .success-icon {
    width: 64px;
    height: 64px;
    background: rgba(34, 197, 94, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #22c55e;
  }

  .company-logo {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    border-radius: 16px;
    overflow: hidden;
  }

  .company-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .logo-placeholder {
    width: 100%;
    height: 100%;
    background: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    font-weight: 700;
  }

  .invite-card h1 {
    margin: 0 0 16px;
    font-size: 24px;
    color: #f8fafc;
  }

  .invite-card h2 {
    margin: 0;
    font-size: 20px;
    color: #f8fafc;
  }

  .invite-message {
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .invite-message strong {
    color: #f8fafc;
  }

  .error-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 8px;
    color: #f87171;
    font-size: 14px;
    margin-bottom: 16px;
  }

  .error-banner.small {
    padding: 8px;
    font-size: 13px;
  }

  .action-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .logged-in-as {
    color: #94a3b8;
    font-size: 14px;
  }

  .btn-primary {
    width: 100%;
    padding: 14px 20px;
    background: #2563eb;
    border: none;
    border-radius: 8px;
    color: white;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    width: 100%;
    padding: 14px 20px;
    background: transparent;
    border: 1px solid #475569;
    border-radius: 8px;
    color: #f8fafc;
    font-size: 15px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-secondary:hover {
    background: #334155;
  }

  .or-divider {
    color: #64748b;
    font-size: 13px;
    margin: 4px 0;
  }

  .login-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .login-form input {
    padding: 14px 16px;
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 8px;
    color: #f8fafc;
    font-size: 15px;
  }

  .login-form input:focus {
    outline: none;
    border-color: #2563eb;
  }

  .expires-note {
    margin-top: 24px;
    font-size: 12px;
    color: #64748b;
  }
`;

export default AcceptInvite;
